<template>
  <div class="show_all_content_wrapper">
    <main>
      <div class="filter_content_wrapper" :class="{ active: filterFormIsActive }">
        <button class="filter_toggler" @click="filterFormIsActive = !filterFormIsActive">
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-select-input
                col="6"
                :optionsList="statusOptions"
                :placeholder="$t('bookingPayouts.status')"
                v-model="filterOptions.status"
              />
              <base-input
                col="6"
                type="number"
                :placeholder="$t('payouts.fields.booking_id')"
                v-model.trim="filterOptions.booking_id"
              />
            </div>
            <div class="btns_wrapper">
              <button class="submit_btn"><i class="fal fa-search"></i></button>
              <button class="reset_btn" type="button" @click="resetFilter">
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("bookingPayouts.title") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:no-data>{{ $t("TABLES.noData") }}</template>
        <template v-slot:[`item.user`]="{ item }">
          {{ item.user && item.user.name ? item.user.name : item.user_id || "-" }}
        </template>
        <template v-slot:[`item.part`]="{ item }">
          {{ partLabel(item.part) }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <span :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <button class="btn_show" @click="showItem(item)">
            <i class="fal fa-eye"></i>
          </button>
        </template>
      </v-data-table>
    </main>

    <div class="pagination_container text-center mt-3 mb-0">
      <v-pagination
        class="py-0"
        square
        v-model="paginations.current_page"
        :length="paginations.last_page"
        :total-visible="6"
        @input="updateRouterQueryParam($event)"
        :prev-icon="getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'"
        :next-icon="getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import payoutLabels from "@/utils/payoutLabels";

export default {
  name: "AllBookingPayouts",
  computed: {
    ...mapGetters({ getAppLocale: "AppLangModule/getAppLocale" }),
    statusOptions() {
      return [
        { id: null, name: this.$t("bookingPayouts.allStatuses") },
        ...payoutLabels.rowStatuses.map((status) => ({
          id: status,
          name: this.statusLabel(status),
        })),
      ];
    },
    tableHeaders() {
      return [
        { text: this.$t("bookingPayouts.id"), value: "id", align: "center", sortable: false },
        { text: this.$t("payouts.fields.booking_number"), value: "booking_number", align: "center", sortable: false },
        { text: this.$t("payouts.fields.user"), value: "user", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.part"), value: "part", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.amount"), value: "amount", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.status"), value: "status", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.auto_release_at"), value: "auto_release_at", align: "center", sortable: false },
        { text: this.$t("TABLES.Admins.actions"), value: "actions", align: "center", sortable: false },
      ];
    },
  },
  data() {
    return {
      loading: false,
      filterFormIsActive: false,
      filterOptions: { status: null, booking_id: null },
      tableRows: [],
      paginations: { current_page: 1, last_page: 1, items_per_page: 10 },
    };
  },
  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },
  methods: {
    statusLabel(status) {
      return payoutLabels.status(this, status);
    },
    partLabel(part) {
      return payoutLabels.part(this, part);
    },
    statusClass(status) {
      return payoutLabels.statusClass(status);
    },
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/booking-payouts/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions = { status: null, booking_id: null };
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/booking-payouts/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    updateRouterQueryParam(page) {
      this.$router.push({ query: { ...this.$route.query, page } });
    },
    async setTableRows() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: "payouts",
          params: {
            page: this.paginations.current_page,
            purpose: "booking_release",
            status: this.filterOptions.status?.id || undefined,
            booking_id: this.filterOptions.booking_id || undefined,
          },
        });
        const payload = res.data.data;
        this.tableRows = Array.isArray(payload) ? payload : payload?.data || [];
        const meta = payload?.meta || res.data.meta || {};
        this.paginations.last_page = meta.last_page || 1;
        this.paginations.items_per_page = meta.per_page || 10;
      } catch (error) {
        console.log(error.response?.data?.message);
      } finally {
        this.loading = false;
      }
    },
    showItem(item) {
      this.$router.push({ path: `/booking-payouts/show/${item.id}` });
    },
  },
  created() {
    if (this.$route.query.page) this.paginations.current_page = +this.$route.query.page;
    this.setTableRows();
  },
};
</script>
